import { useState, useCallback, useMemo } from 'react';
import type {
  ACLAuthorization,
  ACLAuthorizationRequest,
  AccessMode,
} from '@/api/bol/models';
import { getLogisticsObjectsApi } from '@/services/bolApiClient';
import { useSnackbarNotification } from '@/hooks/useSnackbarNotification';
import { extractErrorMessage } from '@/utils/errorUtils';
import { getLogisticsObjectIdKey, type LogisticsObjectEntity } from '@/types/logisticsObject';

interface UseACLResult {
  authorization: ACLAuthorization | null;
  loading: boolean;
  getAuthorization: (memberId: string) => Promise<ACLAuthorization | null>;
  upsertAuthorization: (targetMemberIdentifier: string, mode: AccessMode, authorizedBy: string, aclRevision?: number) => Promise<void>;
  deleteAuthorization: (memberId: string, authorizedBy: string, aclRevision?: number) => Promise<void>;
}

export function useACL(
  logisticsObjectType: LogisticsObjectEntity,
  objectId: string
): UseACLResult {
  const { showSuccess, showError } = useSnackbarNotification();
  const api = useMemo(
    () => getLogisticsObjectsApi(logisticsObjectType),
    [logisticsObjectType],
  );
  const idKey = useMemo(
    () => getLogisticsObjectIdKey(logisticsObjectType),
    [logisticsObjectType],
  );
  const buildIdParams = useCallback(
    () => ({ [idKey]: objectId } as Record<string, string>),
    [idKey, objectId],
  );

  const [authorization, setAuthorization] = useState<ACLAuthorization | null>(null);
  const [loading, setLoading] = useState(false);

  // Get specific authorization by member ID
  const getAuthorization = useCallback(async (memberId: string): Promise<ACLAuthorization | null> => {
    if (!objectId || !memberId) return null;

    setLoading(true);
    try {
      const authData = await api.getLogisticsObjectACLAuthorizationByMemberId({
        ...buildIdParams(),
        memberId,
      });
      setAuthorization(authData);
      return authData;
    } catch (error: unknown) {
      const errorMessage = await extractErrorMessage(error, 'Failed to fetch ACL authorization');
      showError(errorMessage);
      return null;
    } finally {
      setLoading(false);
    }
  }, [api, buildIdParams, objectId, showError]);

  // Create or update an authorization (upsert)
  const upsertAuthorization = useCallback(async (
    targetMemberIdentifier: string, 
    mode: AccessMode,
    authorizedBy: string,
    aclRevision?: number
  ) => {
    if (!objectId || !targetMemberIdentifier) {
      throw new Error('Object ID and member identifier are required');
    }

    if (!authorizedBy) {
      throw new Error('Authorized by member identifier is required');
    }

    setLoading(true);
    try {
      const authRequest: ACLAuthorizationRequest = {
        memberIdentifier: targetMemberIdentifier,
        mode
      };

      await api.upsertLogisticsObjectACLAuthorizationById({
        ...buildIdParams(),
        contentType: 'application/json' as const,
        xBOLMemberIdentifier: authorizedBy,
        aclRevision: aclRevision?.toString(),
        aCLAuthorizationRequest: authRequest,
      });
      
      showSuccess('ACL authorization saved successfully');
    } catch (error: unknown) {
      const errorMessage = await extractErrorMessage(error, 'Failed to save ACL authorization');
      showError(errorMessage);
      throw error;
    } finally {
      setLoading(false);
    }
  }, [api, buildIdParams, objectId, showSuccess, showError]);

  // Delete an authorization
  const deleteAuthorization = useCallback(async (
    memberId: string,
    authorizedBy: string,
    aclRevision?: number
  ) => {
    if (!objectId || !memberId) {
      throw new Error('Object ID and member ID are required');
    }

    if (!authorizedBy) {
      throw new Error('Authorized by member identifier is required');
    }

    setLoading(true);
    try {
      await api.deleteLogisticsObjectACLAuthorizationByMemberId({
        ...buildIdParams(),
        xBOLMemberIdentifier: authorizedBy,
        memberId,
        aclRevision: aclRevision?.toString(),
      });
      
      showSuccess('ACL authorization deleted successfully');
    } catch (error: unknown) {
      const errorMessage = await extractErrorMessage(error, 'Failed to delete ACL authorization');
      showError(errorMessage);
      throw error;
    } finally {
      setLoading(false);
    }
  }, [api, buildIdParams, objectId, showSuccess, showError]);

  return {
    authorization,
    loading,
    getAuthorization,
    upsertAuthorization,
    deleteAuthorization
  };
}
